import { useState, useMemo, useCallback } from "react";

/**
 * Phase 19 — Row-selection engine for bulk admin actions.
 *
 * const sel = useSelection(filteredRows);
 * sel.selected           → Set<id>
 * sel.count              → selected size
 * sel.isSelected(id)     → boolean
 * sel.toggle(id)         → flip one row
 * sel.toggleAll()        → select all/none of the CURRENT visible list
 * sel.clear()            → deselect everything
 * sel.allSelected        → convenience for "all visible rows selected"
 */
export function useSelection(rows) {
  const [selected, setSelected] = useState(() => new Set());
  const isSelected = useCallback((id) => selected.has(id), [selected]);

  const toggle = useCallback((id) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  }, []);

  const toggleAll = useCallback(() => {
    setSelected((prev) => {
      const visibleIds = (rows || []).map((r) => r.id);
      const allIn = visibleIds.length > 0 && visibleIds.every((id) => prev.has(id));
      if (allIn) {
        const next = new Set(prev);
        visibleIds.forEach((id) => next.delete(id));
        return next;
      }
      const next = new Set(prev);
      visibleIds.forEach((id) => next.add(id));
      return next;
    });
  }, [rows]);

  const clear = useCallback(() => setSelected(new Set()), []);

  const allSelected = useMemo(() => {
    const visibleIds = (rows || []).map((r) => r.id);
    return visibleIds.length > 0 && visibleIds.every((id) => selected.has(id));
  }, [rows, selected]);

  const someSelected = selected.size > 0 && !allSelected;

  return { selected, count: selected.size, isSelected, toggle, toggleAll, clear, allSelected, someSelected };
}

// Alias per Phase 19 spec naming
export const useBulkSelection = useSelection;
